import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { cn } from "@/design-system/lib/utils";

// LLM & Reports v3.2 §8.1 step 7 — read-only markdown reader for
// Drafted reports. Phoenix Capital Design System v1.0 § 9
// typography: Montserrat headings, 11pt body at 1.5 line height,
// gold-shadow rule under H3. The styles here approximate what the
// branded .docx output will look like so the user has a faithful
// preview before committing to Apply Branding.
//
// Uses react-markdown + remark-gfm for tables, strikethrough, and
// task lists. The prose styles are inline Tailwind so they survive
// without depending on @tailwindcss/typography.

export interface MarkdownReaderProps {
  markdown: string;
  className?: string;
}

export function MarkdownReader({ markdown, className }: MarkdownReaderProps) {
  return (
    <article
      className={cn(
        "max-w-none",
        "font-body text-body leading-relaxed text-onyx",
        className,
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children, ...props }) => (
            <h1
              {...props}
              className="font-display text-h1 uppercase tracking-[0.05em] text-gold-shadow mt-space-6 mb-space-3"
            >
              {children}
            </h1>
          ),
          h2: ({ children, ...props }) => (
            <h2
              {...props}
              className="font-display text-h2 text-onyx mt-space-5 mb-space-2"
            >
              {children}
            </h2>
          ),
          h3: ({ children, ...props }) => (
            <h3
              {...props}
              className="font-display text-h3 text-gold-shadow mt-space-4 mb-space-2 pb-space-1 border-b border-gold"
            >
              {children}
            </h3>
          ),
          h4: ({ children, ...props }) => (
            <h4 {...props} className="font-display text-h4 text-onyx mt-space-3 mb-space-2">
              {children}
            </h4>
          ),
          p: ({ children, ...props }) => (
            <p {...props} className="my-space-3">
              {children}
            </p>
          ),
          ul: ({ children, ...props }) => (
            <ul {...props} className="my-space-3 ml-space-4 list-disc space-y-1">
              {children}
            </ul>
          ),
          ol: ({ children, ...props }) => (
            <ol {...props} className="my-space-3 ml-space-4 list-decimal space-y-1">
              {children}
            </ol>
          ),
          blockquote: ({ children, ...props }) => (
            <blockquote
              {...props}
              className="my-space-3 pl-space-4 border-l-2 border-gold-shadow italic text-gray-mid"
            >
              {children}
            </blockquote>
          ),
          table: ({ children, ...props }) => (
            <div className="my-space-3 overflow-x-auto">
              <table
                {...props}
                className="min-w-full border-collapse border border-border-light"
              >
                {children}
              </table>
            </div>
          ),
          th: ({ children, ...props }) => (
            <th
              {...props}
              className="bg-onyx text-white-pure px-space-3 py-space-2 text-left font-display text-body font-bold uppercase tracking-[0.04em]"
            >
              {children}
            </th>
          ),
          td: ({ children, ...props }) => (
            <td
              {...props}
              className="px-space-3 py-space-2 border-t border-border-light"
            >
              {children}
            </td>
          ),
          code: ({ children, ...props }) => (
            <code
              {...props}
              className="font-mono text-caption bg-white-warm rounded px-1 py-[1px]"
            >
              {children}
            </code>
          ),
          a: ({ children, href, ...props }) => (
            <a
              href={href}
              {...props}
              className="text-gold-shadow underline hover:text-gold"
              rel="noopener noreferrer"
              target="_blank"
            >
              {children}
            </a>
          ),
        }}
      >
        {markdown}
      </ReactMarkdown>
    </article>
  );
}
